import type { StorefrontEventName } from '@xshop/shared';
import { injectScript, type AppEventDetail, type BrowserApp } from './browserApp';

// Klaviyo's onsite script with the store's public key: Viewed Product, Added to Cart and Started
// Checkout, the browse events Klaviyo's flows start from. Orders are sent by the server instead.
// https://developers.klaviyo.com/en/docs/guide_to_integrating_a_platform_without_a_pre_built_klaviyo_integration

type KlaviyoWindow = Window & { klaviyo?: { track?: (...args: unknown[]) => unknown }; _klOnsite?: unknown[][] };

// Calls klaviyo.js when it has loaded, or queues the call where klaviyo.js reads it on load.
const call = (method: string, ...args: unknown[]): void => {
  const target = window as KlaviyoWindow;
  const loaded = target.klaviyo && typeof target.klaviyo.track === 'function' ? target.klaviyo as Record<string, (...a: unknown[]) => unknown> : null;
  if (loaded?.[method]) loaded[method](...args);
  else (target._klOnsite ??= []).push([method, ...args]);
};

// Loads klaviyo.js for the account's public key, once.
const install = (settings: Record<string, string | boolean>): void => {
  const publicKey = String(settings.publicKey || '');
  if (!/^[A-Za-z0-9]{6}$/.test(publicKey)) return;
  injectScript('xshop-klaviyo', `https://static.klaviyo.com/onsite/js/${publicKey}/klaviyo.js`);
};

// The products of an event in Klaviyo's Items shape.
const itemsOf = (detail: AppEventDetail) => (detail.contents ?? []).map((item) => ({ ProductID: item.id, Quantity: item.quantity, ItemPrice: item.itemPrice }));

// Where the shopper is, and where their checkout is, as Klaviyo's browse and cart templates link to them.
const pageLinks = () => ({ URL: window.location.href, CheckoutURL: `${window.location.origin}/checkout` });

// Sends the browse events Klaviyo flows start from, with the event id the ad pixels used so each counts once.
const track = (name: StorefrontEventName, detail: AppEventDetail): void => {
  const id = detail.eventId ? { $event_id: detail.eventId } : {};
  const { URL, CheckoutURL } = pageLinks();
  if (name === 'product_view') call('track', 'Viewed Product', { ...id, ProductID: detail.productId, ProductName: detail.contentName, Price: detail.value, URL });
  if (name === 'add_to_cart') {
    call('track', 'Added to Cart', { ...id, $value: detail.value, AddedItemProductID: detail.productId, AddedItemProductName: detail.contentName, AddedItemPrice: detail.value, CheckoutURL, Items: itemsOf(detail) });
  }
  if (name === 'begin_checkout') call('track', 'Started Checkout', { ...id, $value: detail.value, ItemCount: detail.numItems, CheckoutURL, Items: itemsOf(detail) });
};

export const klaviyoApp: BrowserApp = { install, track };
